import {ChangeDetectorRef, Component, EventEmitter, inject, Inject, Input, OnDestroy, OnInit, Optional, Output,} from '@angular/core';
import {AbstractControl, ControlContainer, FormControl, NgForm, ValidationErrors} from '@angular/forms';
import {distinctUntilChanged, tap} from 'rxjs/operators';
import {ValidatorService} from '@shared/validator/validator.service';
import {Observable, Subscription} from 'rxjs';
import {BLOCKS_CONFIG, BlocksConfig} from '../../blocks.config';
import {SessionControllerService} from '../../../submission/src/app/session/session-controller.service';
import {ContentLocale} from '@paperlessio/sdk/api/util';
import {LocalizationService} from '@shared/i18n/localization.service';
import {BankAccountInput, BankData} from '@paperlessio/sdk/api/models';

@Component({
  selector: 'app-bank-account-input',
  templateUrl: './bank-account-input.component.html',
  styleUrls: ['./bank-account-input.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class BankAccountInputComponent implements OnInit, OnDestroy {
  private localizationService = inject(LocalizationService);

  @Input() block: BankAccountInput;
  @Input() slug: string;

  @Input() data: BankData = {
    iban: '',
    bic: ''
  };

  @Output() dataChange: EventEmitter<object> = new EventEmitter<object>();

  subs = new Subscription();

  domId = '';

  formControl: FormControl<BankData>;
  ineligible = false;

  constructor(private validatorService: ValidatorService,
              private form: NgForm,
              private cdr: ChangeDetectorRef,
              @Inject(BLOCKS_CONFIG) public blocksConfig: BlocksConfig,
              @Optional() private sessionController: SessionControllerService) {
  }

  ngOnInit() {
    if (this.blocksConfig.submittable) {
      this.domId = 'r' + (this.blocksConfig.editable ? 'e' : '') + (this.block.settings.editable ? 'q' : '');
      this.ineligible = this.sessionController && !this.sessionController.isParticipantAnOwnerOf(this.block.id);
    }

    this.formControl = new FormControl<BankData>(this.data,
      this.block.settings.required ? (control => this._validatePresence(control)) : null,
      (control => this._validate(control)));

    // we have to disable manually to prevent invalid coloring
    if (this.ineligible) {
      this.formControl.disable();
    }

    this.form.form.addControl(this.slug, this.formControl);

    if (this.blocksConfig.submittable || this.blocksConfig.editable) {
      this.subs.add(
        this.formControl.statusChanges.pipe(distinctUntilChanged()).subscribe(status => {
          if (this.data.iban.length > 12 && status === 'VALID') {
            this.validatorService.getBIC(this.data.iban.replace(/ /g, ''))
              .subscribe((res: any) => {
                this.data.bic = res?.bankData?.bic ? res.bankData.bic : '';
                this.dataChange.emit(this.data);
              });
          } else {
            this.data.bic = '';
            this.dataChange.emit(this.data);
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.form.form.removeControl(this.slug);
  }

  onIbanChange(iban: string) {
    if (this.blocksConfig.editable || this.blocksConfig.submittable) {
      this.data.iban = iban;
      this.dataChange.emit(this.data);
      this.formControl.markAsDirty();
      this.formControl.setValue(this.data, {emitViewToModelChange: false});
    }
  }

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  private _validate(control: AbstractControl): Observable<ValidationErrors | null> {
    return this.validatorService.IBAN(control.value.iban).pipe(
      tap(() => this.cdr.markForCheck())
    );
  }

  private _validatePresence(control: AbstractControl) {
    return (control.value?.iban?.length || !this.block.settings.required) ? null : {required: true};
  }
}
